import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const oceanDrift: TemplateRegistration = {
  meta: {
    id: 'ocean-drift',
    name: 'Ocean Drift',
    category: 'Creative',
    description: 'Ocean-inspired design with wavy CSS section dividers',
    previewImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Abril Fatface', 'Hind']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-light wave-top">
        <div class="container">
          <h2 class="section-title">About Us</h2>
          <div class="about-timeline">
            <div class="about-timeline-dot" style="top:4px;"></div>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt wave-top">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url) => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light wave-top">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        const initial = tName.charAt(0);
        return `
          <div class="testimonial-card">
            <div class="testimonial-avatar">${initial}</div>
            <div class="testimonial-content">
              <div class="testimonial-body">
                <p>&ldquo;${tContent}&rdquo;</p>
              </div>
              <div class="testimonial-author">
                <strong>${tName}</strong>
                ${tRole ? `<span>${tRole}</span>` : ''}
                ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
              </div>
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt wave-top">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light wave-top">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt wave-top">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light wave-top">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt wave-top">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-stack">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #0077B6; --bg: #F0F9FF; --bg-alt: #E0F2FE; --accent: #FF8A65;
      --text: #0C4A6E; --text-muted: #4A90A4;
      --font-heading: 'Abril Fatface', serif; --font-body: 'Hind', sans-serif;
      --shadow-sm: 0 2px 8px rgba(0,119,182,.08); --shadow-md: 0 6px 24px rgba(0,119,182,.12);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* WAVE DIVIDERS */
    .wave-top { position: relative; }
    .wave-top::before { content: ''; position: absolute; top: -40px; left: 0; right: 0; height: 40px; background: inherit; clip-path: polygon(0 100%, 100% 100%, 100% 0, 75% 60%, 50% 0, 25% 60%, 0 0); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(240,249,255,.95); backdrop-filter: blur(10px); border-bottom: 1px solid #BAE6FD; box-shadow: var(--shadow-sm); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--primary); }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO - overlapping panels at offsets */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: linear-gradient(180deg, var(--primary), #023E8A); }
    .hero-panels { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 1200px; margin: 0 auto; width: 100%; min-height: 60vh; }
    .hero-panel { position: absolute; border-radius: 16px; padding: 40px 36px; }
    .hero-panel-1 { background: rgba(0,119,182,.6); top: 120px; left: 5%; width: 55%; min-height: 200px; }
    .hero-panel-2 { background: rgba(2,62,138,.5); top: 160px; left: 15%; width: 50%; min-height: 180px; }
    .hero-panel-3 { background: rgba(255,138,101,.15); top: 140px; right: 8%; width: 40%; min-height: 160px; backdrop-filter: blur(8px); }
    .hero-panel-main { position: relative; z-index: 5; padding-top: 120px; text-align: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.8rem, 7vw, 5.5rem); line-height: 1.1; margin-bottom: 20px; color: #fff; }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.2rem); color: rgba(255,255,255,.8); max-width: 600px; margin: 0 auto 36px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--accent); color: #fff; font-family: var(--font-body); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: 0 4px 16px rgba(255,138,101,.3); }
    .hero-cta:hover { transform: translateY(-3px); box-shadow: 0 8px 28px rgba(255,138,101,.4); opacity: 1; color: #fff; }

    /* SECTIONS */
    .section { padding: 120px 0 80px; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3rem); margin-bottom: 40px; color: var(--text); text-align: center; }

    /* ABOUT - timeline-style with vertical line */
    .about-timeline { position: relative; padding-left: 40px; max-width: 800px; margin: 0 auto; }
    .about-timeline::before { content: ''; position: absolute; left: 12px; top: 0; bottom: 0; width: 3px; background: var(--primary); border-radius: 2px; }
    .about-timeline-dot { position: absolute; left: 4px; width: 20px; height: 20px; background: var(--accent); border-radius: 50%; border: 3px solid var(--bg); }
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.9; position: relative; }

    /* SERVICES - horizontal scroll-snap cards */
    .services-grid { display: flex; gap: 24px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 16px; }
    .services-grid::-webkit-scrollbar { height: 6px; }
    .services-grid::-webkit-scrollbar-track { background: var(--bg-alt); border-radius: 3px; }
    .services-grid::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 3px; }
    .service-card { flex: 0 0 300px; scroll-snap-align: start; padding: 36px 28px; background: #fff; border-radius: 16px; box-shadow: var(--shadow-sm); transition: transform .3s, box-shadow .3s; text-align: center; }
    .service-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); }
    .service-icon { margin-bottom: 14px; }
    .icon-char { font-size: 32px; line-height: 1; color: var(--primary); }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.2rem; margin-bottom: 10px; color: var(--text); }
    .service-card p { color: var(--text-muted); font-size: .92rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 14px; font-weight: 700; font-size: 1.1rem; color: var(--accent); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; border-radius: 16px; box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS - blockquote with photo placeholder and decorative quotes */
    .testimonials-grid { display: flex; flex-direction: column; gap: 36px; max-width: 800px; margin: 0 auto; }
    .testimonial-card { padding: 36px 40px; background: #fff; border-radius: 16px; box-shadow: var(--shadow-sm); position: relative; display: flex; gap: 24px; align-items: flex-start; }
    .testimonial-card::before { content: '\\201C'; position: absolute; top: 12px; left: 20px; font-size: 4rem; color: var(--primary); opacity: .2; font-family: Georgia, serif; line-height: 1; }
    .testimonial-avatar { width: 56px; height: 56px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--accent)); display: flex; align-items: center; justify-content: center; color: #fff; font-family: var(--font-heading); font-size: 1.4rem; flex-shrink: 0; }
    .testimonial-content { flex: 1; }
    .testimonial-body p { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 18px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #CBD5E1; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; margin: 0 auto; }
    .faq-item { border-bottom: 1px solid #BAE6FD; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.15rem; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid #BAE6FD; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #BAE6FD; color: var(--text-muted); font-size: .95rem; text-align: center; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #fff; padding: 20px 0; border-bottom: 1px solid #BAE6FD; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-body); padding: 8px 22px; font-size: 1rem; font-weight: 700; border-radius: 50px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - map + form stacked */
    .contact-stack { max-width: 700px; margin: 0 auto; display: flex; flex-direction: column; gap: 32px; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; color: var(--accent); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #fff; border: 1px solid #BAE6FD; border-radius: 12px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--primary); color: #fff; font-family: var(--font-body); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 50px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,119,182,.25); }

    /* FOOTER */
    .footer { background: #023E8A; padding: 40px 0; text-align: center; }
    .footer p { color: #93C5FD; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(240,249,255,.98); border-bottom: 1px solid #BAE6FD; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { flex-wrap: nowrap; }
      .service-card { flex: 0 0 260px; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .testimonial-card { flex-direction: column; }
      .hero-panel { display: none; }
      .hero-panels { padding-top: 100px; }
      .price-row { flex-direction: column; gap: 8px; }
      .wave-top::before { height: 24px; top: -24px; }
      .section { padding: 90px 0 60px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
      .wave-top::before { height: 16px; top: -16px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-panels">
      <div class="hero-panel hero-panel-1"></div>
      <div class="hero-panel hero-panel-2"></div>
      <div class="hero-panel hero-panel-3"></div>
      <div class="hero-panel-main">
        <h1>${safeHeadline}</h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
